import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import { ArrowRight, Sparkles, Loader2, Check, AlertCircle, ChevronDown } from 'lucide-react';

// ── Wave canvas config ────────────────────────────────────────────────────────

interface WaveConfig {
  offset: number;
  amplitude: number;
  frequency: number;
  color: string;
  opacity: number;
}

const GOLD = '#FFD400';

const WAVES: WaveConfig[] = [
  { offset: 0,              amplitude: 60, frequency: 0.003,  color: 'rgba(255,212,0,0.70)',  opacity: 0.40 },
  { offset: Math.PI / 2,    amplitude: 80, frequency: 0.0026, color: 'rgba(255,212,0,0.45)',  opacity: 0.30 },
  { offset: Math.PI,        amplitude: 50, frequency: 0.0034, color: 'rgba(255,212,0,0.30)',  opacity: 0.22 },
  { offset: Math.PI * 1.5,  amplitude: 70, frequency: 0.0022, color: 'rgba(255,255,255,0.12)',opacity: 0.18 },
  { offset: Math.PI * 2,    amplitude: 45, frequency: 0.004,  color: 'rgba(255,255,255,0.08)',opacity: 0.14 },
];

function useWaveCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetMouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let time = 0;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mouseInfluence = reduced ? 10 : 60;
    const influenceRadius = reduced ? 160 : 300;
    const smoothing = reduced ? 0.04 : 0.08;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    const recenter = () => {
      const c = { x: canvas.width / 2, y: canvas.height / 2 };
      mouseRef.current = c;
      targetMouseRef.current = c;
    };
    const onResize = () => { resize(); recenter(); };
    const onMove = (e: MouseEvent) => { targetMouseRef.current = { x: e.clientX, y: e.clientY }; };
    const onLeave = () => recenter();

    resize();
    recenter();
    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    const drawWave = (w: WaveConfig) => {
      ctx.save();
      ctx.beginPath();
      for (let x = 0; x <= canvas.width; x += 4) {
        const dx = x - mouseRef.current.x;
        const dy = canvas.height / 2 - mouseRef.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const inf = Math.max(0, 1 - dist / influenceRadius);
        const mfx = inf * mouseInfluence * Math.sin(time * 0.001 + x * 0.01 + w.offset);
        const y =
          canvas.height / 2 +
          Math.sin(x * w.frequency + time * 0.002 + w.offset) * w.amplitude +
          Math.sin(x * w.frequency * 0.4 + time * 0.003) * (w.amplitude * 0.45) +
          mfx;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.lineWidth = 2;
      ctx.strokeStyle = w.color;
      ctx.globalAlpha = w.opacity;
      ctx.shadowBlur = 30;
      ctx.shadowColor = w.color;
      ctx.stroke();
      ctx.restore();
    };

    const animate = () => {
      time += 1;
      mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * smoothing;
      mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * smoothing;

      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#0B0B0B');
      grad.addColorStop(1, '#080808');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;

      WAVES.forEach(drawWave);
      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(animId);
    };
  }, []);

  return canvasRef;
}

// ── Animation variants ────────────────────────────────────────────────────────

const container: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, staggerChildren: 0.10 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

// ── Component ─────────────────────────────────────────────────────────────────

function SignUpDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-semibold tracking-wide uppercase px-3.5 py-1.5 transition-all duration-150"
        style={{
          background: GOLD,
          color: '#0B0B0B',
          borderRadius: '4px',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#ffe14d'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
      >
        Sign Up
        <ChevronDown className="w-3 h-3" style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 150ms' }} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-44 py-1 z-50"
          style={{
            background: 'rgba(20,20,20,0.96)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '4px',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Link
            to="/signup?role=creator"
            className="block px-4 py-2.5 text-xs font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            onClick={() => setOpen(false)}
          >
            As a Creator
          </Link>
          <Link
            to="/signup?role=vendor"
            className="block px-4 py-2.5 text-xs font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            onClick={() => setOpen(false)}
          >
            As a Vendor
          </Link>
        </div>
      )}
    </div>
  );
}

type FormState = 'idle' | 'loading' | 'success' | 'error';

export default function Home() {
  const canvasRef = useWaveCanvas();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !socialLink.trim()) return;

    setFormState('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/public/match-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim() || undefined,
          social_link: socialLink.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
        setFormState('error');
        return;
      }

      setFormState('success');
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setFormState('error');
    }
  }, [email, phone, socialLink]);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: '#0B0B0B' }}>
      {/* Wave canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />

      {/* Soft glow overlays */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute left-1/2 -translate-x-1/2 top-0 rounded-full blur-[160px]"
          style={{ width: 500, height: 500, background: 'rgba(255,212,0,0.04)' }}
        />
        <div
          className="absolute bottom-0 right-0 rounded-full blur-[120px]"
          style={{ width: 350, height: 350, background: 'rgba(255,212,0,0.025)' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-16">
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="w-full max-w-2xl flex flex-col items-center text-center"
        >
          {/* Top nav: login links */}
          <motion.nav
            variants={item}
            className="w-full flex items-center justify-between mb-16"
          >
            <div className="inline-flex items-baseline gap-0">
              <span
                className="font-display font-bold tracking-tight text-white"
                style={{ fontSize: 'clamp(18px, 3vw, 22px)', letterSpacing: '-0.02em' }}
              >
                CreatorHub
              </span>
              <span
                className="font-display font-bold"
                style={{ fontSize: 'clamp(18px, 3vw, 22px)', color: GOLD, letterSpacing: '-0.02em' }}
              >
                360
              </span>
            </div>

            <div className="flex items-center gap-5">
              <Link
                to="/login"
                className="text-xs font-medium tracking-wide uppercase text-white/40 hover:text-white/70 transition-colors duration-150"
              >
                Login
              </Link>
              <SignUpDropdown />
            </div>
          </motion.nav>

          {/* Badge */}
          <motion.div
            variants={item}
            className="mb-6 inline-flex items-center gap-2 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em]"
            style={{
              border: '1px solid rgba(255,212,0,0.15)',
              borderRadius: '100px',
              color: GOLD,
              background: 'rgba(255,212,0,0.06)',
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            AI-Powered Creator Matching
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={item}
            className="font-display font-bold text-white leading-[1.08] mb-4"
            style={{ fontSize: 'clamp(36px, 7vw, 64px)', letterSpacing: '-0.03em' }}
          >
            Discover your{' '}
            <span style={{ color: GOLD }}>perfect products</span>
          </motion.h1>

          {/* Subhead */}
          <motion.p
            variants={item}
            className="text-base md:text-lg leading-relaxed mb-10 max-w-lg"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            Paste your social media link below. Our AI analyses your content, audience, and brand partnerships to match you with high-converting products.
          </motion.p>

          {/* Match form / success */}
          {formState === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md p-8 text-center"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(74,222,128,0.20)',
                borderRadius: '4px',
              }}
            >
              <div
                className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(74,222,128,0.10)' }}
              >
                <Check className="w-6 h-6 text-mint-400" />
              </div>
              <h3 className="font-display font-semibold text-white text-lg mb-2">
                Request submitted
              </h3>
              <p className="text-white/45 text-sm leading-relaxed">
                We&apos;re analysing your profile now. Results will be sent to{' '}
                <span className="text-white/70">{email}</span> shortly.
              </p>
              <button
                onClick={() => {
                  setFormState('idle');
                  setEmail('');
                  setPhone('');
                  setSocialLink('');
                }}
                className="mt-6 text-xs uppercase tracking-wider font-medium transition-colors duration-150"
                style={{ color: GOLD }}
              >
                Submit another
              </button>
            </motion.div>
          ) : (
            <motion.form
              variants={item}
              onSubmit={handleSubmit}
              className="w-full max-w-md space-y-3"
            >
              {/* Social link — primary input */}
              <div>
                <input
                  type="url"
                  placeholder="Paste your TikTok, Instagram, or YouTube link"
                  value={socialLink}
                  onChange={(e) => setSocialLink(e.target.value)}
                  required
                  className="w-full h-12 px-4 text-sm text-white placeholder:text-white/25 bg-transparent outline-none transition-colors duration-150"
                  style={{
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,212,0,0.30)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
              </div>

              {/* Email + phone row */}
              <div className="flex gap-3">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 h-11 px-4 text-sm text-white placeholder:text-white/25 bg-transparent outline-none transition-colors duration-150"
                  style={{
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,212,0,0.30)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-[42%] h-11 px-4 text-sm text-white placeholder:text-white/25 bg-transparent outline-none transition-colors duration-150"
                  style={{
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '4px',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,212,0,0.30)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
              </div>

              {/* Error */}
              {formState === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-xs py-2 px-3"
                  style={{ color: '#f87171', background: 'rgba(248,113,113,0.06)', borderRadius: '4px' }}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {errorMsg}
                </motion.div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={formState === 'loading'}
                className="w-full h-12 flex items-center justify-center gap-2.5 font-semibold text-sm tracking-wide uppercase transition-all duration-150 disabled:opacity-60"
                style={{
                  background: GOLD,
                  color: '#0B0B0B',
                  borderRadius: '4px',
                  border: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ffe14d'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
              >
                {formState === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Match Now
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-center text-[11px] pt-1" style={{ color: 'rgba(255,255,255,0.22)' }}>
                Free to use · Results delivered to your inbox
              </p>
            </motion.form>
          )}

          {/* Stats / social proof */}
          <motion.div
            variants={item}
            className="mt-14 grid grid-cols-3 gap-6 w-full max-w-sm"
          >
            {[
              { label: 'Creators matched', value: '2,400+' },
              { label: 'Products analyzed', value: '20K+' },
              { label: 'Avg GMV lift', value: '3.2×' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display font-bold text-white text-xl mb-0.5">{s.value}</div>
                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.30)' }}>
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="absolute bottom-6 text-[10px] uppercase tracking-[0.15em]"
          style={{ color: 'rgba(255,255,255,0.15)' }}
        >
          CreatorHub360
        </motion.p>
      </div>
    </div>
  );
}
